import Head from 'next/head'
import { useRouter } from 'next/router'
import { useSearchParams } from 'next/navigation'
import { useContext, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { StoreContext } from 'contexts/StoreContext'
import type { NextPageContext } from 'next'

import { isMobileApp, isiOS, isMobile, safeStringify } from 'utils/utils'
import { HEADER_KEYS } from '../../components/CustomHead'
import CenteredLoader from 'components/common/CenteredLoader'
import {
	modifyQueryParameters,
	buildRedirectionLink,
	openMobileAppWithAppSchemeOrRedirectToStore,
	fetchDeepLink,
	openDeepLink,
} from 'utils/deep-link'

import type { DeepLinkProps } from 'utils/deep-link'
import type { LanguageCode } from 'utils/language'
import { useStores } from 'hooks/useStores'

const DeepLink = inject(
	'Infra',
	'User',
	'Home',
	'Session'
)(
	observer(({ Infra, User, Home, Session, deepLinkId }: DeepLinkProps) => {
		const router = useRouter()
		const queryParams = useSearchParams()
		const clonedQueryParams = new URLSearchParams(queryParams)
		const { setStore } = useContext(StoreContext)
		const { cartStore, addressManagerV2Store } = useStores()

		useEffect(() => {
			const init = async () => {
				const { appParams, eCommerceFooter } = Infra
				const deepLinkData = await fetchDeepLink(deepLinkId, appParams?.c)
				const modifiedQueryParameters = modifyQueryParameters(clonedQueryParams, deepLinkId)
				const deepLink = await buildRedirectionLink({
					queryParameters: modifiedQueryParameters,
					deepLinkData,
					storefrontUrl: appParams?.storefrontUrl,
					language: (User?.preferredLanguage as LanguageCode) || appParams?.l,
					cartStore,
					addressManagerV2Store,
				})

				try {
					const orderType = addressManagerV2Store.formattedOrderTypeForSession()
					const customRef = JSON.parse(new URL(deepLink, 'http://some-domain.com').searchParams.get('ref') || '{}')

					await Session.initOrUpdateSession({
						refObject: { orderType, ...customRef },
						stopLoading: false,
					})
				} catch (err) {
					console.error('failed to init start_web_session')
				}

				const bundleId = appParams?.mobileApp?.bundleId
				const iosApplicationStoreURL = eCommerceFooter?.apps?.ios?.link || null
				const androidApplicationStoreURL = eCommerceFooter?.apps?.android?.link || null
				const isMobileBrowser = isMobile() && !isMobileApp()
				const applicationStoreURL = isiOS() ? iosApplicationStoreURL : androidApplicationStoreURL
				const webOnlyDeepLink = deepLinkData?.webOnly || appParams?.features?.webOnlyDeepLinks

				if (
					applicationStoreURL &&
					bundleId /* "no url/ no bundle id" is equal to "market has no mobile app" */ &&
					isMobileBrowser &&
					!webOnlyDeepLink
				) {
					openMobileAppWithAppSchemeOrRedirectToStore({
						bundleId,
						deepLink,
						applicationStoreURL,
					})
				} else {
					// Inside of the desktop browser or mobile webview
					openDeepLink(deepLink, router, {
						setStore,
						cartStore,
						Infra,
						User,
						Home,
						addressManagerV2Store,
					})
				}
			}

			init()
		}, [])

		return (
			<>
				<Head>
					<meta name="robots" content="noindex,nofollow" key={HEADER_KEYS.robots} />
				</Head>
				<CenteredLoader brand={Infra.appParams?.brand} chainId={Infra.appParams?.c} />
			</>
		)
	})
)

export async function getServerSideProps(context: NextPageContext & { req: { extra: { nextJsError: string } } }) {
	if (context.req && !context.req.extra?.nextJsError) {
		try {
			return {
				props: {
					deepLinkId: context.query?.deepLinkId,
					lang: (context.query.lang as string) || '',
				},
			}
		} catch (e: any) {
			console.error(safeStringify(e))
		}
	}

	if (context.res) {
		context.res.statusCode = 500
		context.res.setHeader('Cache-Control', 'no-store')
	}

	return { props: { lang: null } }
}

export default DeepLink
